import { CircleAlert, CircleCheck, Download, ExternalLink, Send } from 'lucide-react'
import type { ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useConfig } from '../../api/queries'
import type { AnalyzeResult, ReelResult, ToolJob, YoutubeResult } from '../../api/tools'
import { downloadFile, openExternal } from '../../lib/download'
import { plural } from '../../lib/format'
import { formatBytes } from '../../lib/links'
import { Button } from '../Button'
import { Spinner } from '../Spinner'

/** Идёт задача: очередь, шаг и полоска, если шаг считается штуками. `hint` — куда денется итог. */
export function JobProgress({
  job,
  hint = 'Окно можно закрыть — задача останется в списке ниже.',
}: {
  job: ToolJob
  hint?: string
}) {
  const p = job.progress
  const queued = job.status === 'queued'
  const pct = p && p.total > 0 ? Math.round((100 * p.done) / p.total) : null
  return (
    <div className="flex items-start gap-3 rounded-xl bg-surface p-3.5 ring-1 ring-line ring-inset">
      <Spinner size={18} className="mt-0.5 shrink-0 text-muted" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-pretty">
          {queued
            ? job.queue_position && job.queue_position > 1
              ? `В очереди: ${job.queue_position}-я`
              : 'В очереди…'
            : (p?.text ?? 'Начинаю…')}
        </p>
        {pct !== null && (
          <div className="mt-2 h-1 overflow-hidden rounded-full bg-line">
            <div className="h-full rounded-full bg-accent transition-[width]" style={{ width: `${pct}%` }} />
          </div>
        )}
        <p className="mt-1 text-xs text-muted">{hint}</p>
      </div>
    </div>
  )
}

export function JobError({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2.5 rounded-xl bg-danger/10 p-3.5 text-sm text-pretty text-danger">
      <CircleAlert size={18} className="mt-0.5 shrink-0" />
      <p className="min-w-0">{text}</p>
    </div>
  )
}

function Notes({ items }: { items: string[] }) {
  if (items.length === 0) return null
  return (
    <ul className="mt-2 space-y-1 text-xs text-muted">
      {items.map((n) => (
        <li key={n}>• {n}</li>
      ))}
    </ul>
  )
}

/**
 * Карточка поверх исходной страницы. Инструменты скрывают своё окно, пока
 * страница служит фоном: по возврате останутся задача, настройки и прокрутка.
 */
function useOpenPost(): (postId: number) => void {
  const navigate = useNavigate()
  const location = useLocation()
  return (postId) => {
    void navigate(`/post/${postId}`, { state: { backgroundLocation: location } })
  }
}

export function useOpenAnalysis(): (result: AnalyzeResult) => void {
  const navigate = useNavigate()
  const openPost = useOpenPost()
  return (result) => {
    if (result.group_id !== null) void navigate(`/favorites/${result.group_id}`)
    else if (result.post_ids[0] !== undefined) openPost(result.post_ids[0])
  }
}

interface DoneProps {
  job: ToolJob
  compact?: boolean
  /** Зовётся перед переходом к карточке или группе: окно закрывается, а не остаётся над ней. */
  beforeOpen?: () => void
}

const ADDED_HREF = '/?format=video&view=added'

/** Итог разбора и feed_add: у feed_add группы нет, ролики — в «Добавленных». */
export function AnalyzeDone({ job, compact = false, beforeOpen }: DoneProps) {
  const result = job.result as AnalyzeResult
  const navigate = useNavigate()
  const openAnalysis = useOpenAnalysis()
  const open = (r: AnalyzeResult) => {
    beforeOpen?.()
    openAnalysis(r)
  }
  const toFeed = job.kind === 'feed_add'
  const n = result.post_ids.length
  const saved = result.saved_post_ids?.length ?? 0
  const savedText = `${saved} ${plural(saved, 'ролик', 'ролика', 'роликов')}`
  const failedVerb = toFeed
    ? plural(result.failed.length, 'добавилась', 'добавились', 'добавились')
    : plural(result.failed.length, 'разобралась', 'разобрались', 'разобрались')
  return (
    <div>
      {!compact && (
        <p className="flex items-center gap-2 text-sm font-medium">
          <CircleCheck size={18} className="shrink-0 text-muted" />
          {toFeed
            ? saved > 0
              ? `Готово: ${savedText} в «Добавленных»`
              : 'В «Добавленные» ничего не легло'
            : result.group_id !== null
              ? `Готово: ${n} ${plural(n, 'пост', 'поста', 'постов')} в группе «${result.group_name ?? ''}»`
              : 'Готово'}
        </p>
      )}
      {!toFeed && saved > 0 && <p className="mt-1 text-xs text-muted">Сохранено в Залёты: {savedText}.</p>}
      {result.failed.length > 0 && (
        <div className="mt-2 rounded-lg bg-surface p-2.5 text-xs ring-1 ring-line ring-inset">
          <p className="font-medium">
            Не {failedVerb} {result.failed.length} {plural(result.failed.length, 'ссылка', 'ссылки', 'ссылок')}:
          </p>
          <ul className="mt-1 space-y-1">
            {result.failed.map((f) => (
              <li key={f.url} className="break-all text-muted">
                {f.url} — {f.error}
              </li>
            ))}
          </ul>
        </div>
      )}
      <Notes items={result.notes ?? job.notes} />
      {/* Окно добавления открыто над самими «Добавленными»: переходить некуда. */}
      {toFeed
        ? compact &&
          saved > 0 && (
            <Button variant="secondary" size="sm" className="mt-3" onClick={() => void navigate(ADDED_HREF)}>
              Открыть в Залётах
            </Button>
          )
        : n > 0 && (
            <Button variant={compact ? 'secondary' : 'primary'} size="sm" className="mt-3" onClick={() => open(result)}>
              {result.group_id !== null ? 'Открыть группу' : 'Открыть разбор'}
            </Button>
          )}
    </div>
  )
}

function ChatButton() {
  const config = useConfig()
  const bot = config.data?.bot_username
  if (!bot) return null
  return (
    <Button size="sm" onClick={() => openExternal(`https://t.me/${bot}`)}>
      <Send size={14} />
      Открыть чат
    </Button>
  )
}

function Sent({ how, children }: { how: 'sent' | 'link'; children?: ReactNode }) {
  return (
    <p className="flex items-center gap-2 text-sm">
      <Send size={16} className="shrink-0 text-muted" />
      {how === 'sent' ? 'Отправил в чат с ботом.' : 'Файл большой — в чат с ботом ушла ссылка на скачивание.'}
      {children}
    </p>
  )
}

export function ReelDone({ job, compact = false, beforeOpen }: DoneProps) {
  const result = job.result as ReelResult
  const open = useOpenPost()
  const openPost = (id: number) => {
    beforeOpen?.()
    open(id)
  }
  return (
    <div className="space-y-3">
      {result.sent ? (
        <Sent how={result.sent} />
      ) : (
        !compact && (
          <p className="flex items-center gap-2 text-sm font-medium">
            <CircleCheck size={18} className="shrink-0 text-muted" />
            Ролик @{result.handle} готов · {formatBytes(result.size)}
          </p>
        )
      )}
      <div className="flex flex-wrap gap-2">
        {result.download_url && (
          <Button
            size="sm"
            variant={compact ? 'secondary' : 'primary'}
            onClick={() => downloadFile(result.download_url ?? '', `${result.handle}_${result.post_id}.mp4`)}
          >
            <Download size={14} />
            {compact ? 'Скачать' : 'Скачать ещё раз'}
          </Button>
        )}
        {result.sent && !compact && <ChatButton />}
        <Button size="sm" variant="ghost" onClick={() => openPost(result.post_id)}>
          <ExternalLink size={14} />
          Карточка
        </Button>
      </div>
    </div>
  )
}

export function YoutubeDone({ job, compact = false }: { job: ToolJob; compact?: boolean }) {
  const result = job.result as YoutubeResult
  const anySent = result.files.some((f) => f.sent)
  return (
    <div className="space-y-2">
      <ul className="space-y-2">
        {result.files.map((f, i) => (
          <li key={`${f.url}-${i}`} className="flex items-center gap-3 rounded-lg bg-surface p-2.5 ring-1 ring-line ring-inset">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{f.title || f.name || f.url}</p>
              <p className={`text-xs ${f.error ? 'text-danger' : 'text-muted'}`}>
                {f.error ??
                  (f.sent === 'sent'
                    ? 'в чате с ботом'
                    : f.sent === 'link'
                      ? 'в чате ссылка на скачивание'
                      : formatBytes(f.size))}
              </p>
            </div>
            {f.download_url && !f.sent && (
              <Button size="sm" variant="secondary" onClick={() => downloadFile(f.download_url ?? '', f.name ?? undefined)}>
                <Download size={14} />
                Скачать
              </Button>
            )}
          </li>
        ))}
      </ul>
      {!compact && anySent && <ChatButton />}
      {!compact && result.files.some((f) => f.download_url) && (
        <p className="text-xs text-muted">Файлы хранятся сутки.</p>
      )}
    </div>
  )
}

export function JobResult({ job, compact = false, beforeOpen }: DoneProps) {
  if (job.status === 'failed') return <JobError text={job.error ?? 'Не получилось.'} />
  if (job.status !== 'done' || !job.result) return null
  switch (job.kind) {
    case 'analyze':
    case 'feed_add':
      return <AnalyzeDone job={job} compact={compact} beforeOpen={beforeOpen} />
    case 'reel':
      return <ReelDone job={job} compact={compact} beforeOpen={beforeOpen} />
    case 'youtube':
      return <YoutubeDone job={job} compact={compact} />
    default:
      return null
  }
}
